<template>
  <div>
    <div class="d-flex justify-content-between align-items-center mb-3">
      <p class="mb-0 text-muted">
        Save commonly used reviewers for quick reuse.
      </p>
      <b-button variant="primary" size="sm" @click="openCreateModal">
        Add Contact
      </b-button>
    </div>

    <b-skeleton-wrapper :loading="loading">
      <template #loading>
        <b-skeleton width="100%" height="160px"></b-skeleton>
      </template>

      <b-table
        :items="contacts"
        :fields="fields"
        small
        responsive
        head-variant="light"
        empty-text="No reviewer contacts added yet."
      >
        <template #cell(job_title)="row">
          <span>{{ row.item.job_title || "-" }}</span>
        </template>

        <template #cell(phone)="row">
          <span>{{ row.item.phone || "-" }}</span>
        </template>

        <template #cell(actions)="row">
          <div class="d-flex">
            <b-button
              size="sm"
              variant="outline-primary"
              class="mr-2"
              @click="openEditModal(row.item)"
            >
              Edit
            </b-button>
            <b-button
              size="sm"
              variant="outline-danger"
              :disabled="deletingId === row.item.id"
              @click="confirmDelete(row.item)"
            >
              Delete
            </b-button>
          </div>
        </template>
      </b-table>
    </b-skeleton-wrapper>

    <b-modal
      v-model="showModal"
      :title="isEditMode ? 'Edit Reviewer Contact' : 'Add Reviewer Contact'"
      ok-title="Save"
      :ok-disabled="submitting"
      :busy="submitting"
      @ok="submitForm"
      @hidden="resetForm"
    >
      <b-form-group label="Full Name" label-for="reviewer-full-name">
        <b-form-input
          id="reviewer-full-name"
          v-model.trim="form.full_name"
          :state="inputState('full_name')"
          maxlength="255"
          required
        />
        <b-form-invalid-feedback v-if="firstError('full_name')">
          {{ firstError("full_name") }}
        </b-form-invalid-feedback>
      </b-form-group>

      <b-form-group label="Job Role / Title" label-for="reviewer-job-title">
        <b-form-input
          id="reviewer-job-title"
          v-model.trim="form.job_title"
          :state="inputState('job_title')"
          maxlength="255"
        />
        <b-form-invalid-feedback v-if="firstError('job_title')">
          {{ firstError("job_title") }}
        </b-form-invalid-feedback>
      </b-form-group>

      <b-form-group label="Email" label-for="reviewer-email">
        <b-form-input
          id="reviewer-email"
          v-model.trim="form.email"
          :state="inputState('email')"
          type="email"
          maxlength="255"
          required
        />
        <b-form-invalid-feedback v-if="firstError('email')">
          {{ firstError("email") }}
        </b-form-invalid-feedback>
      </b-form-group>

      <b-form-group label="Phone" label-for="reviewer-phone">
        <b-form-input
          id="reviewer-phone"
          v-model.trim="form.phone"
          :state="inputState('phone')"
          type="tel"
          maxlength="50"
          required
        />
        <b-form-invalid-feedback v-if="firstError('phone')">
          {{ firstError("phone") }}
        </b-form-invalid-feedback>
      </b-form-group>
    </b-modal>
  </div>
</template>

<script>
import Providers from "@/providers";

const DEFAULT_FORM = () => ({
  full_name: "",
  job_title: "",
  email: "",
  phone: "",
});

export default {
  name: "ReviewerContactsManager",
  data() {
    return {
      loading: false,
      submitting: false,
      deletingId: null,
      showModal: false,
      contacts: [],
      editingId: null,
      form: DEFAULT_FORM(),
      errors: {},
      fields: [
        { key: "full_name", label: "Full Name" },
        { key: "job_title", label: "Job Title" },
        { key: "email", label: "Email" },
        { key: "phone", label: "Phone" },
        { key: "actions", label: "Actions", class: "reviewer-contact-actions" },
      ],
    };
  },
  computed: {
    isEditMode() {
      return this.editingId !== null;
    },
  },
  methods: {
    loadContacts() {
      this.loading = true;
      Providers.profile
        .getReviewerContacts()
        .then((items) => {
          this.contacts = items;
        })
        .catch((resp) => {
          this.$httpError("Failed to load reviewer contacts", resp);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    openCreateModal() {
      this.resetForm();
      this.showModal = true;
    },
    openEditModal(item) {
      this.errors = {};
      this.editingId = item.id;
      this.form = {
        full_name: item.full_name || "",
        job_title: item.job_title || "",
        email: item.email || "",
        phone: item.phone || "",
      };
      this.showModal = true;
    },
    resetForm() {
      this.editingId = null;
      this.form = DEFAULT_FORM();
      this.errors = {};
      this.submitting = false;
    },
    inputState(field) {
      if (!Object.prototype.hasOwnProperty.call(this.errors, field)) {
        return null;
      }
      return false;
    },
    firstError(field) {
      const fieldErrors = this.errors[field];
      return Array.isArray(fieldErrors) && fieldErrors.length > 0
        ? fieldErrors[0]
        : null;
    },
    validateClientSide() {
      const errors = {};

      if (!this.form.full_name) {
        errors.full_name = ["Full name is required."];
      }
      if (!this.form.email) {
        errors.email = ["Email is required."];
      }
      if (!this.form.phone) {
        errors.phone = ["Phone is required."];
      }

      this.errors = errors;
      return Object.keys(errors).length === 0;
    },
    submitForm(event) {
      event.preventDefault();

      if (!this.validateClientSide()) {
        return;
      }

      this.submitting = true;
      this.errors = {};

      const payload = {
        full_name: this.form.full_name,
        job_title: this.form.job_title || null,
        email: this.form.email,
        phone: this.form.phone,
      };

      const request = this.isEditMode
        ? Providers.profile.updateReviewerContact(this.editingId, payload)
        : Providers.profile.createReviewerContact(payload);

      request
        .then(() => {
          this.$success(
            this.isEditMode ? "Contact Updated" : "Contact Added",
            this.isEditMode
              ? "Reviewer contact updated successfully."
              : "Reviewer contact added successfully."
          );
          this.showModal = false;
          this.loadContacts();
        })
        .catch((resp) => {
          this.errors = resp?.body?.errors || {};
          this.$httpError(
            this.isEditMode
              ? "Failed to update reviewer contact"
              : "Failed to add reviewer contact",
            resp
          );
        })
        .finally(() => {
          this.submitting = false;
        });
    },
    async confirmDelete(item) {
      const confirmed = await this.$bvModal.msgBoxConfirm(
        `Delete reviewer contact for ${item.full_name}?`,
        {
          title: "Delete Reviewer Contact",
          okTitle: "Delete",
          okVariant: "danger",
          cancelTitle: "Cancel",
          centered: true,
        }
      );

      if (!confirmed) {
        return;
      }

      this.deletingId = item.id;
      Providers.profile
        .deleteReviewerContact(item.id)
        .then(() => {
          this.$success(
            "Contact Deleted",
            "Reviewer contact deleted successfully."
          );
          this.loadContacts();
        })
        .catch((resp) => {
          this.$httpError("Failed to delete reviewer contact", resp);
        })
        .finally(() => {
          this.deletingId = null;
        });
    },
  },
  created() {
    this.loadContacts();
  },
};
</script>

<style scoped>
.reviewer-contact-actions {
  min-width: 150px;
}
</style>
